import moment from "moment";
import { useContext, useEffect, useState } from "react";
import {
	Button
} from "react-bootstrap";
import { withTranslation } from "react-i18next";
import { BiPlus } from "react-icons/bi";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { LoaderContext, RegulationContext } from "../../../../App";
import ConfirmationModal from "../../../../components/ConfirmationModal/ConfirmationModal";
import Pagination from "../../../../components/Pagination/Pagination";
import { DEFAULT_ITEMS_PER_PAGE, FormType, LS_ACTIVE_ASSESSMENT_LIST_KEY, LS_AUTHORITY_KEY, SORT_ORDER_DESC } from "../../../../core/constants/common";
import useHasPermission from "../../../../core/hooks/useHasPermission";
import FormService from "../../../../core/service/FormService";
import {
	getCompanyInfoFromStorage,
	getListActiveAssessmentsPeriodParams,
} from "../../../../core/utilities/assessmentPeriodUtils";
import assessment_pic from "../../../../images/Assessment.png";
import "../Forms.scss";
import { create_dce, getDuplicateDCEPath, update_dce } from "../CreateDataCollectionEvent/CreateDataCollectionEvent";
import AssessmentExpiryEditModal from "./AssessmentExpiryEditModal";
import AssessmentListCard from "./AssessmentListCard";
import AssessmentReminderModal from "./AssessmentReminderModal";
import AssessmentReshareModal from "./AssessmentReshareModal";
import SendReminderModal from "./SendReminderModal";
import DCEQuestionPermissionsModal from "./DCEQuestionPermissionsModal";

function QuantitativeDCEList({ t, searchKey, updatedOn, filters = {} }) {
	const lsData = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
	const lsSelfValue = localStorage.getItem(LS_ACTIVE_ASSESSMENT_LIST_KEY);
	const location = useLocation();
	const navigate = useNavigate();

	const [itemsTotalCount, setItemTotalCount] = useState(0);
	const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
	const [currentPage, setCurrentPage] = useState(1);
	const [order, setOrder] = useState(SORT_ORDER_DESC);
	const [sortField, setSortField] = useState('reportingPeriod');
	const [forms, setForms] = useState([]);
	const { glbRegulation } = useContext(RegulationContext);
	const [selfAssessment, setSelfAssessment] = useState(lsSelfValue ? lsSelfValue === 'self' : true);
	const [sharedAssessmentId, setSharedAssessmentId] = useState(0);
	const [remindedAssessmentId, setRemindedAssessmentId] = useState(0);
	// Reopened when coming back from the reshare dialog's "Go to permission"
	const [assessmentToReshare, setAssessmentToReshare] = useState(location?.state?.reshareAssessment ?? { id: 0 });
	const [assessmentEditedForExpiry, setAssessmentEditedForExpiry] = useState({ id: 0 });
	const [lastSubmissionDate, setLastSubmissionDate] = useState(null);
	const [dceToRemind, setDCEToRemind] = useState({id: 0});
	const [questionPermAssessment, setQuestionPermAssessment] = useState({ id: 0, label: '' });
	const { loaderDisplay, setLoaderDisplay } = useContext(LoaderContext);
	const glbRegulationId = glbRegulation?.id ? glbRegulation.id : "";
	const { hasDataCollectionUpdatePermission } = useHasPermission();

	useEffect(() => {
		// Fire request when regulation or search key changes
		// Only fire if we've already initialized (updatedOn > 0 means filters are set up)
		if (updatedOn > 0) {
			getForms(0, itemsPerPage, searchKey, sortField, order);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [glbRegulationId, searchKey]);

	useEffect(() => {
		// This effect handles initial load (when filters are loaded from localStorage or defaults)
		// and subsequent filter changes
		if (updatedOn === 0) return;

		// Reset pagination to page 1 when filters change
		setCurrentPage(1);
		// Fetch from page 0 (which will be converted to page 1 in getForms)
		getForms(0, itemsPerPage, searchKey, sortField, order);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updatedOn, filters]);

	const getForms = (
		pageNumber,
		itemsPerPageVal,
		searchVal = "",
		sortField,
		orderVal,
		assessmentBool = selfAssessment
	) => {
		if (pageNumber > 0) {
			pageNumber = pageNumber - 1;
		} else {
			setCurrentPage(1);
		}
		const perPage = itemsPerPageVal > 0 ? itemsPerPageVal : itemsPerPage;
		const sort = sortField + "," + orderVal;
		setLoaderDisplay(true);
		
		// EXPIRED is a client-side concept (lastSubmissionDate < end-of-day today).
		// Strip it from the API param; when active, fetch all forms (null submissionStatus)
		// so expired forms of any backend status are included for client-side filtering.
		const rawStatuses = filters?.submissionStatus ? filters.submissionStatus.split(",") : [];
		const isExpiredFilterActive = rawStatuses.includes("EXPIRED");
		const backendStatuses = rawStatuses.filter(s => s !== "EXPIRED");
		let submissionStatus = isExpiredFilterActive ? null : (backendStatuses.length > 0 ? backendStatuses.join(",") : null);

		const companyInfo = getCompanyInfoFromStorage();
		const { year, periodStart, periodEnd } = getListActiveAssessmentsPeriodParams(
			filters?.year,
			companyInfo
		);
		// When filtering for expired forms, fetch a large batch (sorted newest-first means
		// past-period forms appear on later pages) so all periods are included for client-side filtering.
		const effectivePerPage = isExpiredFilterActive ? 500 : perPage;
		const effectivePage = isExpiredFilterActive ? 0 : pageNumber;
		FormService.listActiveAssessments(
			effectivePage,
			effectivePerPage,
			sort,
			'GENERAL',
			assessmentBool,
			submissionStatus,
			true,
			year,
			periodStart,
			periodEnd
		).then((response) => {
			setLoaderDisplay(false);
			let data = [...response.data];
			let totalCount = parseFloat(response.headers["x-total-count"]);

			if (isExpiredFilterActive) {
				const now = new Date();
				data = data.filter(form => {
					const completedCount = form.submissionStatusCount?.COMPLETED ?? 0;
					const draftCount = form.submissionStatusCount?.DRAFT ?? 0;
					const inProgressCount = (form.submissionStatusCount?.IN_PROGRESS ?? 0)
						+ (form.submissionStatusCount?.CORRECTION_PENDING ?? 0)
						+ (form.submissionStatusCount?.PENDING_APPROVAL ?? 0);
					const isCompleted = form.sharedOn && inProgressCount === 0 && draftCount === 0 && completedCount > 0;
					// Append T23:59:59 so date-only strings are treated as end-of-day local time,
					// not UTC midnight (which would trigger false expiry for UTC+ timezones).
					const deadline = new Date((form.lastSubmissionDate || "").replace(/T.*/, "") + "T23:59:59");
					const isExpired = !isCompleted && deadline < now;

					if (isExpired) return true;

					// If other statuses are also selected, include forms matching those criteria
					if (backendStatuses.length > 0) {
						const isFormNotStarted = !form.sharedOn;
						const isFormInProgress = form.sharedOn && !isCompleted;
						return (backendStatuses.includes("COMPLETED") && isCompleted) ||
							(backendStatuses.includes("IN_PROGRESS") && isFormInProgress) ||
							(backendStatuses.includes("NOT_STARTED") && isFormNotStarted);
					}

					return false;
				});
				totalCount = data.length;
			}

			setItemTotalCount(totalCount);
			setForms(data);
		}).catch((error) => { setLoaderDisplay(false); });
	};

	const onShareAssessment = (confirmed) => {
		if (!confirmed) {
			setSharedAssessmentId(0);
			return;
		}
		FormService.shareAssessment(sharedAssessmentId).then(res => {
			setSharedAssessmentId(0);
			if (res.status !== 200) return;

			toast.success("Success", {
				position: toast.POSITION.TOP_RIGHT,
			});
			getForms(currentPage, itemsPerPage, searchKey, sortField, order)
		}).catch((error) => { setSharedAssessmentId(0); });
	};

	const blankCardToRender = () => {
		const hasSubmissionStatusFilter = filters?.submissionStatus !== null && filters?.submissionStatus !== undefined;
		
		return (
			<div className="Assessment_blankcard">
				<div>
					<div className="assessment_pic">
						<img src={assessment_pic} />
					</div>
					{hasSubmissionStatusFilter ? (
						<h4 className="mb-4">{t('NoDataAvailableForFilter.label') || 'There is no data available for the selected filter'}</h4>
					) : (
						<>
							{
								selfAssessment
									?
									<h4 className="mb-4">{t('SelfAssessmentBlankHeader1.label')}</h4>
									:
									<>
										<h4>{t('SupplierAssessmentBlankHeader.label')}</h4>
										<p>{t('SupplierAssessmentBlankText.label')}</p>
									</>
							}
							<Button variant="primary" className="px-3" hidden={!hasDataCollectionUpdatePermission()} onClick={() => navigate(create_dce, { state: { selfAssessment } })}>
								<BiPlus className="mr-2" /> {t("New.label")}
							</Button>
						</>
					)}
				</div>
			</div>
		);
	};

	const onSendReminder = (confirmed, payload) => {
		if (!confirmed) {
			setDCEToRemind({ id: 0});
			return;
		}
		FormService.sendReminder(dceToRemind.id, payload).then(res => {
			if (res.status !== 200) return;

			toast.success("Success", {
				position: toast.POSITION.TOP_RIGHT,
			});
			setDCEToRemind({ id: 0});
		}).catch((error) => { 
			setDCEToRemind({ id: 0});
		});
	};

	// Drop the one-time reshare reopen from the route state so a refresh does not reopen the dialog
	useEffect(() => {
		if (!location?.state?.reshareAssessment) return;

		const { reshareAssessment, ...state } = location.state;
		navigate(`${location.pathname}${location.search}`, { replace: true, state: state });
	}, []);

	return (
		<>
			<div className="FormsList">

				<div className="List_Table_01 mt-2">

					<div className={selfAssessment === true ? 'Assessment_Company' : 'Assessment_Supplier'}>
						{!loaderDisplay && forms.length === 0 && blankCardToRender()}
						{
							!loaderDisplay && forms.length > 0 &&
							<div className={selfAssessment === true ? 'Assessment_Company_listbox' : 'Assessment_Supplier_listbox'}>
								{
									forms.map((form, index) => {
										return <AssessmentListCard key={`assessment-form-card-${index + 1}`}
											form={form}
											formType={FormType.GENERAL}
											selfAssessment={selfAssessment}
											onPermBubbleClick={() => {
												navigate(update_dce.replace("<id>", form.id), { state: { selfAssessment, openPermissions: true } });
											}}
											onShareClick={() => {
												setSharedAssessmentId(form.id);
											}}
											onReshareClick={() => {
												setAssessmentToReshare(form);
											}}
											onQuickRemindClick={() => {
												console.log(form);
												setDCEToRemind(form);
											}}
											onEditAssessment={() => {
												navigate(update_dce.replace("<id>", form.id), { state: { selfAssessment } });
											}}
											onExpiryEdit={() =>  setAssessmentEditedForExpiry(form)}
											onDuplicate={() => {
												navigate(getDuplicateDCEPath(form.id), { state: { selfAssessment } });
											}}
											onReminderSetup={() =>  {
												setRemindedAssessmentId(form.id);
												setLastSubmissionDate(form.lastSubmissionDate);
											}}
											onShowQuestionPermissions={() => {
												setQuestionPermAssessment({
													id: form.id,
													label: `${form.assessmentForm?.name || ''} — ${form.period ? moment(form.period).format('MMM, YYYY') : ''}`
												});
											}}
										/>
									})
								}
							</div>
						}
					</div>
					{
						forms?.length > 0 &&
						<div className="p-2 List_footer_count">
							<Pagination
								itemsTotalCount={itemsTotalCount}
								itemsPerPage={itemsPerPage}
								currentPage={currentPage}
								setCurrentPage={setCurrentPage}
								alwaysShown={true}
								setItemsPerPage={setItemsPerPage}
								pageClicked={(pageNum, perPage) => {
									getForms(pageNum, perPage, searchKey, sortField, order);
								}}
							/>
						</div>
					}
				</div>
			</div >
			{
				sharedAssessmentId > 0 &&
				<ConfirmationModal
					modalShow={true}
					title={t('ShareAssessment.label')}
					messageText={t('WouldYouLikeToShareItNow.label')}
					callbackModal={(val) => onShareAssessment(val)}
					confirmBtn={t("ShareNow.label")}
					CancelBtn={t("DoItLater.label")}
				/>
			}
			{
				remindedAssessmentId > 0 &&
				<AssessmentReminderModal id={remindedAssessmentId}
					onHide={(saved) => {
						setRemindedAssessmentId(0);
						if (!saved) return;

						getForms(currentPage, itemsPerPage, searchKey, sortField, order);
					}}
					lastSubmissionDate={lastSubmissionDate} />
			}
			{
				assessmentToReshare?.id > 0 &&
				<AssessmentReshareModal assessment={assessmentToReshare}
					selfAssessment={selfAssessment}
					onHide={() => setAssessmentToReshare({ id: 0 })}
					onSuccess={() => {
						setAssessmentToReshare({ id: 0 });
						getForms(currentPage, itemsPerPage, searchKey, sortField, order);
					}}
				/>

			}
			{
				assessmentEditedForExpiry?.id > 0 &&
				<AssessmentExpiryEditModal assessment={assessmentEditedForExpiry}
					onHide={() => setAssessmentEditedForExpiry({ id: 0 })}
					onSuccess={() => {
						setAssessmentEditedForExpiry({ id: 0 });
						getForms(currentPage, itemsPerPage, searchKey, sortField, order);
					}} />
			}
			{
				dceToRemind.id > 0 &&
				<SendReminderModal
					modalShow={true}
					dce={dceToRemind}
					callbackModal={(confirmed, payload) => {
						setDCEToRemind({ id: 0});
						if (confirmed) {
							onSendReminder(confirmed, payload);
						}
					}}
				/>
			}
			{
				questionPermAssessment.id > 0 &&
				<DCEQuestionPermissionsModal
					show={true}
					assessmentId={questionPermAssessment.id}
					assessmentLabel={questionPermAssessment.label}
					onHide={() => setQuestionPermAssessment({ id: 0, label: '' })}
				/>
			}
		</>
	);
}

QuantitativeDCEList.propTypes = {};

export default withTranslation()(QuantitativeDCEList);
